import { useEffect, useRef, useState } from "react";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import CTLTable from "application/components/CTLTable";
import { getColumns, Attributes, operationURLS } from "./model";
import CTLAdvancedFilter from "application/components/CTLAdvancedFilter";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import MDTypography from "components/MDTypography";
import { application } from "globals/endpoints";
import MDSnackbar from "components/MDSnackbar";
import CTLNotification from "application/components/CTLNotification";

function Comparison({
  onLoad,
  pageId,
  searchText,
  registerCallBacks,
  pageName,
}) {
  const [controller, dispatch] = useMaterialUIController();
  const { activePage, tableSortColumn, tableColumnDataType, tableColumnDirection } = controller;
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [name, setName] = useState("All Configuration");
  const [show, setShow] = useState(null);
  const [defaultButton, setDefaultButton] = useState("onAllList");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);

  // Track the grid's live sort column and sortDataType so the export sends the
  // same SearchRequest as the last /list fetch. The backend only applies the
  // free-text search predicate when sortDataType is non-empty (string/integer/
  // boolean/long); with the default "" the search is silently skipped — the root
  // cause of the TC-13/TC-14 test failure (grid showed 4 rows, CSV had all 27).
  const [sortColumn, setSortColumn] = useState("extension");
  const [columnDataType, setColumnDataType] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    tableSortColumn !== "" && setSortColumn(tableSortColumn);
    tableColumnDataType !== "" && setColumnDataType(tableColumnDataType);
    tableColumnDirection !== "" && setSortDir(tableColumnDirection);
  }, [tableSortColumn, tableColumnDataType, tableColumnDirection]);

  // Latest-ref pattern: the DownloadData callback is registered once in
  // registerCallBacks (stable), but must call the current DownloadTableData
  // (which closes over advancedFilters, searchText, sortColumn, etc.). Assigning
  // downloadFnRef.current on every render keeps it fresh without re-registering.
  const downloadFnRef = useRef(null);
  // Separate ref for the detailed export — decoupled from grid state (task #9791).
  const downloadDetailedFnRef = useRef(null);

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  useEffect(() => {
    Attributes && onLoad(Attributes);
    registerCallBacks &&
      registerCallBacks({
        openAdvancedFilter: onOpenFilter,
        onNoDefaultComparison: DefaultComparison,
        onDefaultComparison: NoDefaultComparison,
        onAllList: onAllList,
        DownloadData: () => downloadFnRef.current && downloadFnRef.current(),
        DownloadDetailedData: () => downloadDetailedFnRef.current && downloadDetailedFnRef.current(),
      });
  }, [activePage]);

  function onOpenFilter() {
    setOpenFilter(true);
  }

  function onCloseFilter() {
    setOpenFilter(false);
  }

  function onApplyFilter(appliedFilters) {
    // Preserve the selected report mode (All/Default/Changed) together with any
    // applied advanced filter (task #9612). The mode is carried as a synthetic
    // "difference" filter; merge it with the user's applied filters instead of
    // replacing, so mode + filters stay together for both the grid and the
    // export (TC-04/TC-05 with TC-06/TC-12).
    let modeValue = "";
    if (show === "DefaultComparison") {
      modeValue = "yes";
    } else if (show === "NoDefaultComparison") {
      modeValue = "no";
    }
    const userFilters = (appliedFilters || []).filter(
      (filter) => filter.fieldName && filter.fieldName !== "difference"
    );
    let mergedFilters;
    if (modeValue) {
      mergedFilters = [
        { direction: "", dataType: "", fieldName: "difference", value: modeValue },
        ...userFilters,
      ];
    } else {
      mergedFilters = userFilters.length
        ? userFilters
        : [{ direction: "", dataType: "", fieldName: "", value: "" }];
    }
    setAdvancedFilters(mergedFilters);
    setOpenFilter(false);
  }

  function DefaultComparison() {
    const yesData = [
      { direction: "", dataType: "", fieldName: "difference", value: "yes" },
    ];
    setName("Default Configuration ");
    setAdvancedFilters(yesData);
    setShow("DefaultComparison");
    setDefaultButton(null);
    onLoad({ ...Attributes, downloadButtonTitle: "Download Default Configuration Comparison Report" });
  }

  function NoDefaultComparison() {
    const noData = [
      { direction: "", dataType: "", fieldName: "difference", value: "no" },
    ];
    setName("Change Configuration");
    setAdvancedFilters(noData);
    setShow("NoDefaultComparison");
    setDefaultButton(null);
    onLoad({ ...Attributes, downloadButtonTitle: "Download Changed Configuration Comparison Report" });
  }

  function onAllList() {
    const AllData = [{ direction: "", dataType: "", fieldName: "", value: "" }];
    setName("All Configuration");
    setAdvancedFilters(AllData);
    setShow("onAllList");
    setDefaultButton("onAllList");
    onLoad({ ...Attributes, downloadButtonTitle: "Download All Configuration Comparison Report" });
  }

  // Default and NoDefault And All Data Download

  function DownloadTableData() {
    // Send the IDENTICAL SearchRequest that the grid's last /list fetch used.
    // sortColumn and columnDataType are tracked from the MUI context
    // (tableColumnDataType / tableSortColumn), which CTLTableContent writes via
    // setTableColumnDataType dispatch whenever the user clicks a column header.
    // Without this, sortDataType:"" causes the backend to skip the free-text
    // search predicate entirely, returning all rows while the grid shows only the
    // searched subset (TC-13/TC-14 regression).
    const requestBody = {
      sortBy: sortColumn,
      sortDirection: sortDir === "none" ? "asc" : sortDir,
      search: searchText || "",
      sortDataType: columnDataType,
      advancedFilters:
        advancedFilters && advancedFilters.length
          ? advancedFilters
          : [{ direction: "", dataType: "", fieldName: "difference", value: "" }],
    };
    const fileName = `${(name || "Comparison").trim()} Comparison Report.csv`;
    application
      .post(operationURLS.export, requestBody, { responseType: "arraybuffer" })
      .then((response) => {
        const blob = new Blob([response.data], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
        setNotify({
          isOpen: true,
          message: Attributes.messages.success,
          type: "success",
          pagename: pageName,
          status: 200,
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          status: error?.response?.status,
          message: Attributes.messages.error,
          type: "error",
          pagename: pageName,
        });
      });
  }

  // Keep ref current so the stable DownloadData callback registered once in
  // registerCallBacks always invokes the latest DownloadTableData closure.
  downloadFnRef.current = DownloadTableData;

  // Detailed export — decoupled from grid state: no sortBy/search/advancedFilters.
  // Always downloads the full phone/agent/fax population (task #9791).
  function DownloadDetailedTableData() {
    application
      .post(operationURLS.exportDetailed, {}, { responseType: "arraybuffer" })
      .then((response) => {
        const blob = new Blob([response.data], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "Detailed Extension Configuration Report.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
        setNotify({
          isOpen: true,
          message: Attributes.messages.success,
          type: "success",
          pagename: pageName,
          status: 200,
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          status: error?.response?.status,
          message: Attributes.messages.error,
          type: "error",
          pagename: pageName,
        });
      });
  }

  downloadDetailedFnRef.current = DownloadDetailedTableData;

  const renderSuccessSB = (
    <MDSnackbar
      color="success"
      icon="check"
      title={`${pageName}`}
      content={`${pageName} ${Attributes.messages.success}`}
      open={successSB}
      onClose={closeSuccessSB}
      close={closeSuccessSB}
    />
  );

  const renderErrorSB = (
    <MDSnackbar
      color="error"
      icon="warning"
      title={`${pageName}`}
      content={`${pageName} ${Attributes.messages.error}`}
      open={errorSB}
      onClose={closeErrorSB}
      close={closeErrorSB}
    />
  );
  return (
    <MDBox>
      <Card sx={{ minWidth: 275 }}>
        <CardContent>
          <MDTypography sx={{ textAlign: "center" }}>{name}</MDTypography>
        </CardContent>
      </Card>

      <CTLTable
        apiURL={operationURLS.list}
        advancedFilters={advancedFilters}
        columns={getColumns()}
        entriesPerPage={false}
        canSearch
        defaultSortDirection="asc"
        defaultSortColumn="extension"
        pageId={pageId}
        searchText={searchText}
        hasRowSelection={false}
      />
      <CTLAdvancedFilter
        entityName="Nodes"
        columns={getColumns()}
        applyFilter={openFilter}
        onApplyFilter={onApplyFilter}
        onCloseFilter={onCloseFilter}
      />
      <CTLNotification notify={notify} setNotify={setNotify} />
      {renderSuccessSB}
      {renderErrorSB}
    </MDBox>
  );
}

export default Comparison;
